import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { throttle } from "lodash";

import { WidgetContext } from "./WidgetContext";
import { useBreakpoint } from "./WidgetBreakpoint";

import { Colours } from "@learningblocks/shared-components";

import LineChart from "./LineChart";
import Button from "./Button";
import WidgetControls from "./WidgetControls";
import Dialog from "./Dialog";

import Background from "./img/bg.svg";
import chevronWhite from "./img/chevronWhite.svg";
import play_icon from "./img/play-icon-grey.svg";
import pause_icon from "./img/pause-icon-grey.svg";
import InactiveArrow from "./img/widgetLock-arrow-inactive.svg";
import ActiveArrow from "./img/widgetLock-arrow-active.svg";

export default function Widget(props) {
  // Include the widget providers
  const breakpoints = useBreakpoint();
  const widget = useContext(WidgetContext);

  const [chartsCollapsed] = useState(true);
  // useEffect(()=>{
  //   alert(breakpoints.md)
  //   if(!breakpoints.md){
  //     widget.setNavigationExpanded(true)
  //   }
  // },[breakpoints.md])
  const [chartSelection, setChartSelection] = useState("Linear scale");

  const [widgetArea, setWidgetArea] = useState({
    height: 0,
    width: 0,
  });
  useEffect(() => {
    const throttledHandleResize = throttle(() => {
      setWidgetArea({
        height: widget.widgetArea.current.getBoundingClientRect().height,
        width: widget.widgetArea.current.getBoundingClientRect().width,
      });

      // console.log(widgetArea.height)
      //alert(window.innerHeight)
    }, 10);

    throttledHandleResize();

    window.addEventListener("resize", throttledHandleResize);
    return () => window.removeEventListener("resize", throttledHandleResize);
  }, [widget.widgetArea.current, props.visible]);

  const selected = {
    border: "none",
    background: "white",
    color: Colours.navy1,
    padding: "8px",
    borderRadius: "5px",
    width: "50%",
    textAlign: "center",
    cursor: "pointer",
  };

  const notSelected = {
    border: "none",
    width: "50%",
    background: "transparent",
    color: Colours.navy3,
    padding: "8px",
    textAlign: "center",
    cursor: "pointer",
  };

  return (
    <>
      <div
        style={{
          background: !props.inline ? "#003E6B" : null,
          backgroundImage: !props.inline ? `url(${Background})` : null,
          backgroundSize: "cover",
          width: "calc(100vw - 17px)",
          height: "100vh",
          display: "flex",
          position: "absolute",
          top: "0px",
          left: "0px",
        }}
        className="widgetContent"
      >
        <div
          className="widgetWrapper"
          style={{
            width: "100%",
            overflowX: "hidden",
            overflowY: "scroll",
            display: "block",
            flexDirection: "column",
          }}
        >
          <img
            src={InactiveArrow}
            alt="Previous section"
            style={{
              display:
                props.widgetType === "PCR2" || !props.inline ? "none" : "block",
              height: "45px",
              margin: "auto",
              marginTop: "16px",
              cursor: "pointer",
            }}
            id="prev-btn"
            tabIndex={0}
            onClick={() => {
              props.toPrevious();
            }}
          />
          <div
            style={{
              background: Colours.navy2,
              maxWidth: "1000px",
              margin: "16px 16px 0",
              border: "1px solid " + Colours.navy4,
              boxSizing: "border-box",
              borderRadius: "5px",
              display: !breakpoints.md ? "none" : null,
              flexDirection: "row",
              width: "calc(100% - 32px)",
            }}
          >
            <button
              style={{
                cursor: "pointer",
                border: "none",
                background: "transparent",
                width: "100%",
                padding: "16px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
              onClick={() => widget.setNavigationExpanded(true)}
            >
              <div style={{ width: "100%" }}>
                <p
                  style={{
                    color: "white",
                    textAlign: "left",
                    margin: 0,
                    fontSize: "16px",
                  }}
                >
                  {"Number of cycles: " + widget.cycleCounter}
                </p>
                <p
                  style={{
                    color: "white",
                    textAlign: "left",
                    margin: "8px 0 0",
                    fontSize: "16px",
                  }}
                >
                  {"Process efficiency: " + widget.efficiencyCounter + "%"}
                </p>
              </div>
              <img
                src={chevronWhite}
                style={{ transform: "rotate(90deg)" }}
                alt
              />
            </button>
          </div>
          <div
            className="chart-type-select"
            style={{
              background: "#002F52",
              maxWidth: "1000px",
              margin: breakpoints.md ? "16px 16px 0" : "16px auto 0",
              border: "1px solid " + Colours.navy4,
              boxSizing: "border-box",
              borderRadius: "5px",
              display: widgetArea.height < 678 ? "flex" : "none",
              flexDirection: "row",
              width: "calc(100% - 32px)",
            }}
          >
            <button
              style={chartSelection == "Linear scale" ? selected : notSelected}
              onClick={() => setChartSelection("Linear scale")}
            >
              Linear scale
            </button>
            <button
              style={
                chartSelection == "Logarithmic Scale" ? selected : notSelected
              }
              onClick={() => setChartSelection("Logarithmic Scale")}
            >
              Logarithmic scale
            </button>
          </div>
          <div
            ref={widget.widgetArea}
            className="widgetCharts"
            style={{
              position: "relative",
              display: "block",
              width: breakpoints.md ? "calc(100% - 32px)" : "calc(100% - 96px)",
              maxWidth: "1000px",
              // height: "calc(100% - 32px)",
              padding: breakpoints.md ? "16px" : "32px",
              overflowY: "hidden",
              alignItems: "center",
              flexDirection: "column",
              justifyContent: "space-around",
              margin: "0 auto",
            }}
          >
            {(chartsCollapsed && chartSelection == "Linear scale") ||
            widgetArea.height >= 678 ? (
              <LineChart // Linear Scale
                width={700}
                height={270}
                type="linear"
                xAxisLabel="Cycles"
                yAxisLabel="Copies (Linear scale)"
                cycleCounter={widget.cycleCounter}
                fluidXAxis={widget.fluidXAxis}
                fluidYAxis={widget.fluidYAxis}
                totalData={widget.totalData}
                desiredData={widget.desiredData}
                overhangData={widget.overhangData}
                line1Display={widget.totalDNAline}
                line2Display={widget.desiredRegionLine}
                line3Display={widget.overhangLine}
                visible={props.visible}
              />
            ) : null}
            {(chartsCollapsed && chartSelection == "Logarithmic Scale") ||
            widgetArea.height >= 678 ? (
              <LineChart // Log Scale
                width={700}
                height={270}
                type="log"
                xAxisLabel="Cycles"
                yAxisLabel="Copies (Logarithmic scale)"
                cycleCounter={widget.cycleCounter}
                fluidXAxis={widget.fluidXAxis}
                fluidYAxis={widget.fluidYAxis}
                totalData={widget.totalData}
                desiredData={widget.desiredData}
                overhangData={widget.overhangData}
                line1Display={widget.totalDNAline}
                line2Display={widget.desiredRegionLine}
                line3Display={widget.overhangLine}
                visible={props.visible}
              />
            ) : null}
          </div>
          <div
            style={{
              maxWidth: "1000px",
              margin: breakpoints.md ? "0 16px 16px" : "0 auto 16px",
              display: breakpoints.md ? "block" : "none",
              width: "calc(100% - 32px)",
            }}
          >
            <Button type="Card-secondary" onClick={() => widget.playCount()}>
              {!widget.countPlaying ? (
                <>
                  <img
                    src={play_icon}
                    alt="Play icon"
                    style={{ marginRight: "8px" }}
                  />
                  <span>Run cycle</span>
                </>
              ) : (
                <>
                  <img
                    src={pause_icon}
                    alt="Pause icon"
                    style={{ marginRight: "8px" }}
                  />
                  <span>Pause cycle</span>
                </>
              )}
            </Button>
          </div>
          <img
            src={ActiveArrow}
            alt="Next section"
            className="widget-next-btn"
            style={{
              display:
                props.widgetType === "PCR2" || !props.inline ? "none" : "block",
              height: "45px",
              margin: "auto",
              marginBottom: "16px",
              cursor: "pointer",
              transform: "rotate(180deg)",
            }}
            tabIndex={0}
            onClick={() => {
              props.toNext();
            }}
          />
        </div>
        <WidgetControls inline={props.inline} />
      </div>
      <Dialog type="Intro-Screen" display={widget.displayIntro} />
    </>
  );
}

Widget.propTypes = {
  widgetData: PropTypes.object,
  // inline: PropTypes.boolean,
  // visible: PropTypes.boolean,
  usersnap: PropTypes.string,
};
